import { useLayoutEffect, useState } from 'react'

export interface Rect {
  top: number
  left: number
  width: number
  height: number
}

const sameRect = (a: Rect, b: Rect) =>
  a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height

const sameRects = (a: Rect[] | null, b: Rect[]) =>
  a != null && a.length === b.length && a.every((r, i) => sameRect(r, b[i]))

/**
 * Track the targets' viewport rects (rAF polling — targets can move with
 * layout). `null` = not measured yet; `[]` = none of the selectors matched.
 */
export function useTargetRects(selectors: string[]): Rect[] | null {
  const [rects, setRects] = useState<Rect[] | null>(null)
  // The array prop is rebuilt every render — key the effect on the joined
  // string and re-split inside so it only re-runs when the step changes.
  const key = selectors.join('\n')

  useLayoutEffect(() => {
    const list = key.split('\n')
    let raf = 0
    let last: Rect[] | null = null
    const measure = () => {
      // Element presence is the signal (a zero rect is still "present", which
      // also keeps jsdom tests meaningful); missing targets fall back to a
      // centered card rather than stranding the user on an invisible step.
      const next: Rect[] = []
      for (const sel of list) {
        const r = document.querySelector(sel)?.getBoundingClientRect()
        if (r) next.push({ top: r.top, left: r.left, width: r.width, height: r.height })
      }
      if (!sameRects(last, next)) {
        last = next
        setRects(next)
      }
      raf = requestAnimationFrame(measure)
    }
    // Synchronous first measure: no one-frame flash of the new copy at the
    // previous step's spotlight position.
    measure()
    return () => cancelAnimationFrame(raf)
  }, [key])

  return rects
}
